import {
  ClientError,
  Variables as DefaultVariables,
  RequestDocument,
  RequestOptions,
} from 'graphql-request';
import {
  APQ_GET_HEADERS,
  buildPersistedQueryUrl,
  getPersistedQueryExtension,
  resolveDocument,
  shouldRetryWithDocument,
} from './apq';

export type { DefaultVariables };
export const defaultUrl =
  process.env.NODE_ENV === 'test'
    ? `${process.env.NEXT_PUBLIC_WEB_ENV}/api/graphql`
    : (process.env.NEXT_PUBLIC_GRAPHQL_API_URL ?? '/api/graphql');

export const appendHeaders = (
  headers: Headers | [string, string][] | Record<string, string> | undefined,
  newHeaders: Record<string, string>,
): Record<string, string> => {
  const headersObj = new Headers(headers);
  Object.entries(newHeaders).forEach(([key, value]) => {
    headersObj.set(key, value);
  });

  // Convert Headers back to plain object
  const result: Record<string, string> = {};
  headersObj.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

export type GraphQLRequestProps<Variables extends DefaultVariables = DefaultVariables> =
  RequestOptions<Variables> & {
    url?: string;
    locale: string;
  };

export type GraphQLRequestWithTokenProps<Variables extends DefaultVariables = DefaultVariables> =
  GraphQLRequestProps<Variables> & {
    idToken: string | null;
  };

export interface GraphQLResult<Data> {
  data: Data;
  extensions?: any;
  headers: Headers;
  status: number;
}

const parseResponseBody = async (response: Response) => {
  const body = await response.text();

  if (response.headers.get('Content-Type')?.includes('application/json')) {
    try {
      return JSON.parse(body);
    } catch {
      return body;
    }
  }

  return body;
};

/**
 * Executes a GraphQL operation over `fetch`, using Automatic Persisted Queries.
 * Hashed queries go out as a GET so the CDN can cache them; the document only
 * travels on the one POST that registers it, and on mutations.
 *
 * Mirrors `graphql-request` v4 semantics exactly — it resolves with
 * `{ ...result, headers, status }` and throws its `ClientError` on a non-2xx
 * response, a GraphQL error, or a missing `data` — so callers that inspect
 * `error.response.errors` keep working.
 */
const execute = async <Data = any, Variables extends DefaultVariables = DefaultVariables>({
  url,
  document,
  variables,
  requestHeaders,
  signal,
}: {
  url: string;
  document: RequestDocument;
  variables?: Variables;
  requestHeaders?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<GraphQLResult<Data>> => {
  const { query, operationName, isMutation } = resolveDocument(document);
  const extensions =
    process.env.NODE_ENV === 'development' ? undefined : await getPersistedQueryExtension(query);

  const get = (target: string) =>
    fetch(target, {
      method: 'GET',
      headers: appendHeaders(requestHeaders, APQ_GET_HEADERS),
      signal,
    });

  const post = (body: Record<string, unknown>) =>
    fetch(url, {
      method: 'POST',
      headers: appendHeaders(requestHeaders, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
      signal,
    });

  const persistedUrl = extensions
    ? buildPersistedQueryUrl(url, { operationName, variables, extensions, isMutation })
    : null;

  // Falls back to POST for mutations and over-length URLs, which still send the
  // hash alone; only a document we could not hash at all travels in full.
  const postBody = extensions
    ? { operationName, variables, extensions }
    : { query, operationName, variables };

  let response = persistedUrl ? await get(persistedUrl) : await post(postBody);
  let result = await parseResponseBody(response);

  // Cache miss: register the document once over POST, then every later request
  // for it is a bare GET.
  if (extensions && shouldRetryWithDocument(result?.errors)) {
    response = await post({ query, operationName, variables, extensions });
    result = await parseResponseBody(response);
  }

  if (response.ok && !result?.errors && result?.data) {
    return { ...result, headers: response.headers, status: response.status };
  }

  throw new ClientError(
    {
      ...(typeof result === 'string' ? { error: result } : result),
      status: response.status,
      headers: response.headers,
    },
    { query, variables },
  );
};

export const rawRequest = async <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>({
  url = defaultUrl,
  locale,
  ...rest
}: GraphQLRequestProps<Variables>) => {
  if (!locale) {
    return;
  }

  return execute<Data, Variables>({
    url: url as string,
    // Callers reach `rawRequest` with either key; graphql-request used `query`.
    document: rest.document ?? (rest as any).query,
    variables: rest.variables,
    signal: rest.signal as AbortSignal | undefined,
    requestHeaders: appendHeaders(rest.requestHeaders as any, {
      'Accept-Language': locale,
    }),
  });
};

export const rawRequestWithToken = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>({
  requestHeaders,
  idToken,
  ...rest
}: GraphQLRequestWithTokenProps<Variables>) => {
  return rawRequest<Data, Variables>({
    requestHeaders: idToken
      ? appendHeaders(requestHeaders as any, { Authorization: idToken })
      : (requestHeaders as any),
    ...(rest as unknown as GraphQLRequestProps<Variables>),
  } as any);
};

export const rawRequestWithTokenRequired = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>(
  props: GraphQLRequestWithTokenProps<Variables>,
) => {
  if (!props.idToken) {
    return;
  }
  return rawRequestWithToken<Data, Variables>(props);
};

// Deliberately not `async`: callers rely on the pre-APQ signature
// `Promise<Data> | undefined` and assert on the promise (`await request(x)!`).
export const request = <Data = any, Variables extends DefaultVariables = DefaultVariables>({
  url = defaultUrl,
  locale,
  requestHeaders,
  ...rest
}: GraphQLRequestProps<Variables>) => {
  if (!locale) {
    return;
  }

  return execute<Data, Variables>({
    url: url as string,
    document: rest.document ?? (rest as any).query,
    variables: rest.variables,
    signal: rest.signal as AbortSignal | undefined,
    requestHeaders: appendHeaders(requestHeaders as any, {
      'Accept-Language': locale,
    }),
  }).then(({ data }) => data);
};

export const requestWithToken = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>({
  requestHeaders,
  idToken,
  ...rest
}: GraphQLRequestWithTokenProps<Variables>) =>
  request<Data, Variables>({
    requestHeaders: idToken
      ? appendHeaders(requestHeaders as any, { Authorization: idToken })
      : requestHeaders,
    ...(rest as unknown as GraphQLRequestProps<Variables>),
  });

export const requestWithTokenRequired = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>(
  props: GraphQLRequestWithTokenProps<Variables>,
) => {
  if (!props.idToken) {
    return;
  }
  return requestWithToken<Data, Variables>(props);
};
